import { motion, AnimatePresence } from 'framer-motion';
import { X, MessageSquare, BarChart3 } from 'lucide-react';
import { useDippyStore } from '@/stores/dippy-store';
import DippyPanelChat from './DippyPanelChat';
import DippyPanelStatus from './DippyPanelStatus';
import { cn } from '@/lib/utils';

interface DippyPanelProps {
  isOpen: boolean;
  dippyName: string;
  avatar: 'seedling' | 'robot';
}

export default function DippyPanel({ isOpen, dippyName, avatar }: DippyPanelProps) {
  const { activeTab, setTab, closePanel } = useDippyStore();

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="absolute bottom-20 left-0 w-80 h-[28rem] bg-white rounded-xl shadow-xl border border-stone-200 flex flex-col overflow-hidden"
          data-testid="dippy-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-stone-100 bg-stone-50">
            <div className="flex items-center gap-2">
              <span className="text-base">{avatar === 'seedling' ? '🌱' : '🤖'}</span>
              <span className="text-sm font-semibold text-stone-800">{dippyName}</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={closePanel}
                className="p-1 text-stone-400 hover:text-stone-600 rounded"
                aria-label="Close panel"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Tab bar */}
          <div className="flex border-b border-stone-100">
            <TabButton
              active={activeTab === 'chat'}
              onClick={() => setTab('chat')}
              icon={<MessageSquare className="w-3.5 h-3.5" />}
              label="Chat"
            />
            <TabButton
              active={activeTab === 'status'}
              onClick={() => setTab('status')}
              icon={<BarChart3 className="w-3.5 h-3.5" />}
              label="Status"
            />
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden">
            {activeTab === 'chat' ? (
              <DippyPanelChat dippyName={dippyName} />
            ) : (
              <DippyPanelStatus />
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors',
        active
          ? 'text-green-700 border-b-2 border-green-600 bg-green-50/50'
          : 'text-stone-500 hover:text-stone-700 hover:bg-stone-50'
      )}
    >
      {icon}
      {label}
    </button>
  );
}
